import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, TrendingUp, Users, Wallet, PieChart, Edit2, Filter, Calendar, GitBranch, BarChart3, Target, Clock, DollarSign, Award, Percent, CreditCard, Skull } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FinancialReports } from './FinancialReports';
import { projectsApi, jvAgreementsApi, ledgerApi, subdivisionsApi, notesApi, reportsApi, adminApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import {
  generatePerformanceReport as buildPerformanceReport,
  generateNotesReport as buildNotesReport,
  generateDeadDealsReport as buildDeadDealsReport,
  generatePortfolioReport as buildPortfolioReport,
  generateCapitalBreakdownReport as buildCapitalBreakdownReport,
  generateCommissionReport as buildCommissionReport,
  generateJVSummaryReport as buildJVSummaryReport,
  generateSubdivisionReport as buildSubdivisionReport,
} from '../lib/reports';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#1A2F23', '#C68E5D', '#3B82F6', '#8B5CF6', '#06B6D4', '#22C55E', '#F97316', '#EF4444', '#6366F1', '#EC4899'];

const RADIAN = Math.PI / 180;
const renderSmartLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.05) return null;
  const radius = outerRadius + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#555" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
      {name} ({(percent * 100).toFixed(0)}%)
    </text>
  );
};

export const Reports = () => {
  const [projects, setProjects] = useState([]);
  const [jvAgreements, setJvAgreements] = useState([]);
  const [portfolioReport, setPortfolioReport] = useState(null);
  const [capitalReport, setCapitalReport] = useState(null);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [notesReport, setNotesReport] = useState(null);
  const [deadDealsReport, setDeadDealsReport] = useState(null);
  const [deadDealsPeriod, setDeadDealsPeriod] = useState('all_time');
  const [deadDealsYear, setDeadDealsYear] = useState(new Date().getFullYear());
  const [deadDealsCustomStart, setDeadDealsCustomStart] = useState('');
  const [deadDealsCustomEnd, setDeadDealsCustomEnd] = useState('');
  const [loadingDeadDeals, setLoadingDeadDeals] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Report configuration dialog state
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [pendingReport, setPendingReport] = useState(null);
  const [reportConfig, setReportConfig] = useState({
    name: '',
    dateFrom: '',
    dateTo: '',
    scope: 'all', // 'all' or 'selected'
    selectedProjects: []
  });
  
  // Subdivision report state
  const [showSubdivisionDialog, setShowSubdivisionDialog] = useState(false);
  const [selectedSubdivisionParent, setSelectedSubdivisionParent] = useState(null);
  const [subdivisionFinancials, setSubdivisionFinancials] = useState(null);
  const [loadingSubdivision, setLoadingSubdivision] = useState(false);

  useEffect(() => {
    loadData();
    // Intentional mount-only fetch. loadData is defined inline and only reads
    // module-level API objects + state setters, so it's effectively stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, jvRes, ledgerRes, portfolioRes, capitalRes, notesReportRes, deadDealsRes] = await Promise.all([
        projectsApi.getAll(),
        jvAgreementsApi.getAll(),
        ledgerApi.getSummary(),
        reportsApi.getPortfolio().catch(() => ({ data: null })),
        reportsApi.getCapital().catch(() => ({ data: null })),
        notesApi.getReport().catch(() => ({ data: null })),
        reportsApi.getDeadDeals().catch(() => ({ data: null }))
      ]);
      setProjects(projectsRes.data);
      setJvAgreements(jvRes.data);
      setLedgerSummary(ledgerRes.data);
      setPortfolioReport(portfolioRes.data);
      setCapitalReport(capitalRes.data);
      if (!portfolioRes.data || !capitalRes.data) {
        toast.error('Some report data failed to load — try refreshing');
      }
      setNotesReport(notesReportRes.data);
      setDeadDealsReport(deadDealsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Load dead deals with period filter
  const loadDeadDeals = async (period, year, startDate, endDate) => {
    setLoadingDeadDeals(true);
    try {
      const params = { period };
      if (period === 'year' && year) params.year = year;
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const res = await reportsApi.getDeadDeals(params);
      setDeadDealsReport(res.data);
    } catch (error) {
      console.error('Failed to load dead deals:', error);
    } finally {
      setLoadingDeadDeals(false);
    }
  };

  // Reload dead deals when period changes. Includes the custom date range
  // so switching dates while period='custom' triggers a refetch (genuine
  // bug fix from the iteration-92.5 code review pass).
  useEffect(() => {
    if (!loading) {
      loadDeadDeals(deadDealsPeriod, deadDealsYear, deadDealsCustomStart, deadDealsCustomEnd);
    }
    // loadDeadDeals is stable (defined inline, reads only module API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadDealsPeriod, deadDealsYear, deadDealsCustomStart, deadDealsCustomEnd, loading]);

  // ── ALL report math is server-side (canonical /api/reports/* endpoints).
  // These are direct reads of the server payloads — no client recomputation.
  const metrics = portfolioReport?.performance || null;
  const capitalByParty = capitalReport?.by_party || null;
  const capitalBreakdown = capitalReport?.expenses_by_category || [];
  const totalCapitalUsed = capitalReport?.total_expenses || 0;

  // Thin wrappers around lib/reports.js — actual jsPDF construction lives there
  const generatePerformanceReport = () => buildPerformanceReport(metrics);

  const generateNotesReport = () => buildNotesReport(notesReport);

  const generateDeadDealsReport = () => buildDeadDealsReport(deadDealsReport);

  // Open config dialog for report
  const openReportConfig = (reportType, defaultName) => {
    setPendingReport(reportType);
    setReportConfig({
      name: defaultName,
      dateFrom: '',
      dateTo: '',
      scope: 'all',
      selectedProjects: []
    });
    setShowConfigDialog(true);
  };

  const toggleProjectSelection = (projectId) => {
    setReportConfig(prev => ({
      ...prev,
      selectedProjects: prev.selectedProjects.includes(projectId)
        ? prev.selectedProjects.filter(id => id !== projectId)
        : [...prev.selectedProjects, projectId]
    }));
  };

  // Build query params for the canonical report endpoints from the dialog config
  const buildReportParams = () => {
    const params = {};
    if (reportConfig.dateFrom) params.date_from = reportConfig.dateFrom;
    if (reportConfig.dateTo) params.date_to = reportConfig.dateTo;
    if (reportConfig.scope === 'selected' && reportConfig.selectedProjects.length > 0) {
      params.project_ids = reportConfig.selectedProjects.join(',');
    }
    return params;
  };

  const handleGenerateReport = async () => {
    setShowConfigDialog(false);
    const params = buildReportParams();

    try {
      switch (pendingReport) {
        case 'portfolio': {
          const res = await reportsApi.getPortfolio(params);
          buildPortfolioReport(reportConfig.name, res.data);
          break;
        }
        case 'capital': {
          const res = await reportsApi.getCapital(params);
          buildCapitalBreakdownReport(reportConfig.name, res.data);
          break;
        }
        case 'commission':
          buildCommissionReport(reportConfig.name, ledgerSummary);
          break;
        case 'jv': {
          const res = await reportsApi.getPortfolio(params);
          buildJVSummaryReport(reportConfig.name, res.data.jv_summary, res.data.filters?.date_range_label);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    }
    setPendingReport(null);
  };

  // Get subdivision parent projects
  const subdivisionParents = projects.filter(p => p.strategy === 'Subdivide' && !p.parent_project_id);

  const loadSubdivisionFinancials = async (parentProjectId) => {
    setLoadingSubdivision(true);
    try {
      const res = await subdivisionsApi.getConsolidatedFinancials(parentProjectId);
      setSubdivisionFinancials(res.data);
    } catch (error) {
      console.error('Failed to load subdivision financials:', error);
      toast.error('Failed to load subdivision data');
    } finally {
      setLoadingSubdivision(false);
    }
  };

  const openSubdivisionReport = (parentProject) => {
    setSelectedSubdivisionParent(parentProject);
    setSubdivisionFinancials(null);
    setShowSubdivisionDialog(true);
    loadSubdivisionFinancials(parentProject.id);
  };

  const generateSubdivisionReport = () => {
    if (!subdivisionFinancials) return;
    buildSubdivisionReport(subdivisionFinancials);
    setShowSubdivisionDialog(false);
  };

  const projectsWithJV = projects.filter(p => 
    jvAgreements.some(jv => jv.project_id === p.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Reports</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Generate custom reports, financial statements, and performance analysis</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              const r = await adminApi.downloadMetricAudit();
              const blob = r.data instanceof Blob ? r.data : new Blob([r.data]);
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'LandFlow_Metric_Audit.xlsx';
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Metric audit workbook downloaded');
            } catch (e) {
              toast.error('Could not download audit workbook');
            }
          }}
          className="rounded-sm text-xs"
          data-testid="download-metric-audit-btn"
          title="Download an Excel workbook listing every metric LandFlow computes, with its module location and formula — for auditing against your QuickBooks ground truth."
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          Metric Audit (.xlsx)
        </Button>
      </div>

      <Tabs defaultValue="reports" className="w-full">
        <TabsList>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial">Financial Statements</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-6 mt-4">

      {/* Performance Metrics Dashboard */}
      {metrics && (
        <Card className="rounded-sm border" data-testid="performance-metrics">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Performance Metrics
                </CardTitle>
                <CardDescription>Key performance indicators across {metrics.total_deals} closed deals</CardDescription>
              </div>
              <Button
                onClick={generatePerformanceReport}
                variant="outline"
                size="sm"
                className="rounded-sm"
              >
                <Download className="w-4 h-4 mr-1" /> Export PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <div className="p-4 bg-white rounded-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Net Profit</span>
                </div>
                <p className="text-xl font-mono font-bold text-emerald-600">{formatCurrency(metrics.total_net_profit)}</p>
                <p className="text-[10px] text-muted-foreground">Gross: {formatCurrency(metrics.total_gross_profit)}</p>
              </div>
              
              <div className="p-4 bg-white rounded-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-blue-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Avg Profit/Deal</span>
                </div>
                <p className="text-xl font-mono font-bold text-blue-600">{formatCurrency(metrics.avg_profit)}</p>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-sm border border-purple-100">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Avg Days to Close</span>
                </div>
                <p className="text-xl font-mono font-bold text-purple-600">{metrics.avg_days_held} days</p>
              </div>
              
              <div className="p-4 bg-white rounded-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <Percent className="w-4 h-4 text-amber-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Avg ROI</span>
                </div>
                <p className="text-xl font-mono font-bold text-amber-600">{(metrics.avg_roi || 0).toFixed(1)}%</p>
              </div>
              
              <div className="p-4 bg-forest-50 rounded-sm border border-forest-100">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-forest-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Win Rate</span>
                </div>
                <p className="text-xl font-mono font-bold text-forest-600">{(metrics.win_rate || 0).toFixed(0)}%</p>
                <p className="text-xs text-muted-foreground">{metrics.profitable_deals}/{metrics.total_deals} profitable</p>
              </div>
              
              <div className="p-4 bg-rose-50 rounded-sm border border-rose-100">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-rose-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">YTD Profit</span>
                </div>
                <p className="text-xl font-mono font-bold text-rose-600">{formatCurrency(metrics.ytd_profit)}</p>
                <p className="text-xs text-muted-foreground">{metrics.ytd_deals} deals this year</p>
              </div>
            </div>

            {/* Notable Deals & Strategy Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Notable Deals */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notable Deals</h4>
                <div className="space-y-2">
                  {metrics.best_deal && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border">
                      <div>
                        <p className="text-xs text-muted-foreground">Best Profit</p>
                        <p className="font-medium">{metrics.best_deal.name}</p>
                      </div>
                      <span className="font-mono font-semibold text-emerald-600">{formatCurrency(metrics.best_deal.profit)}</span>
                    </div>
                  )}
                  {metrics.fastest_deal && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border">
                      <div>
                        <p className="text-xs text-muted-foreground">Fastest Close</p>
                        <p className="font-medium">{metrics.fastest_deal.name}</p>
                      </div>
                      <span className="font-mono font-semibold text-blue-600">{metrics.fastest_deal.days_held} days</span>
                    </div>
                  )}
                  {metrics.slowest_deal && metrics.slowest_deal.id !== metrics.fastest_deal?.id && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border">
                      <div>
                        <p className="text-xs text-muted-foreground">Longest Hold</p>
                        <p className="font-medium">{metrics.slowest_deal.name}</p>
                      </div>
                      <span className="font-mono font-semibold text-amber-600">{metrics.slowest_deal.days_held} days</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Strategy Breakdown */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Performance by Strategy</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-sm bg-forest-500" />
                      <div>
                        <p className="font-medium">Flip</p>
                        <p className="text-xs text-muted-foreground">{metrics.flip_deals} deals</p>
                      </div>
                    </div>
                    <span className="font-mono font-semibold">{formatCurrency(metrics.flip_profit)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-sm bg-terracotta" />
                      <div>
                        <p className="font-medium">Subdivide</p>
                        <p className="text-xs text-muted-foreground">{metrics.subdivide_deals} deals</p>
                      </div>
                    </div>
                    <span className="font-mono font-semibold">{formatCurrency(metrics.subdivide_profit)}</span>
                  </div>
                </div>
                
                {/* Average Deal Metrics */}
                <div className="mt-4 p-3 bg-muted/20 rounded-sm">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Avg Purchase Price</p>
                      <p className="font-mono font-medium">{formatCurrency(metrics.avg_deal_size)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Avg Sale Price</p>
                      <p className="font-mono font-medium">{formatCurrency(metrics.avg_sale_price)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capital by Party Summary */}
      {capitalByParty && (capitalByParty.total_jv_capital > 0 || capitalByParty.land_finder_net_cost_basis > 0 || capitalByParty.shared_expenses > 0) && (
        <Card className="rounded-sm border" data-testid="capital-by-party-card">
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
              <Users className="w-4 h-4" />
              Capital by Party
            </CardTitle>
            <CardDescription>Investment breakdown between Investor/Partner and Land Finder</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Investor Column */}
              <div className="p-4 bg-white rounded-sm border">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-3">Investor/Partner</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">JV Capital</span>
                    <span className="font-mono">{formatCurrency(capitalByParty.total_jv_capital)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expenses</span>
                    <span className="font-mono">{formatCurrency(capitalByParty.investor_expenses)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">EMD</span>
                    <span className="font-mono">{formatCurrency(capitalByParty.investor_emd)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Less: Reimb.</span>
                    <span className="font-mono">({formatCurrency(capitalByParty.investor_reimbursements)})</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-200 font-semibold">
                    <span>Net Cost Basis</span>
                    <span className="font-mono text-blue-700">{formatCurrency(capitalByParty.investor_net_cost_basis)}</span>
                  </div>
                </div>
              </div>
              
              {/* Land Finder Column */}
              <div className="p-4 bg-terracotta-50 rounded-sm border border-terracotta-100">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-terracotta-700 mb-3">Land Finder</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expenses</span>
                    <span className="font-mono">{formatCurrency(capitalByParty.land_finder_expenses)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">EMD</span>
                    <span className="font-mono">{formatCurrency(capitalByParty.land_finder_emd)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Less: Reimb.</span>
                    <span className="font-mono">({formatCurrency(capitalByParty.land_finder_reimbursements)})</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-terracotta-200 font-semibold">
                    <span>Net Cost Basis</span>
                    <span className="font-mono text-terracotta-700">{formatCurrency(capitalByParty.land_finder_net_cost_basis)}</span>
                  </div>
                </div>
              </div>
              
              {/* Shared/Total Column */}
              <div className="p-4 bg-muted/50 rounded-sm border">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Shared & Total</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Unassigned Expenses</span>
                    <span className="font-mono">{formatCurrency(capitalByParty.shared_expenses)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t font-semibold">
                    <span>Total Cost Basis</span>
                    <span className="font-mono text-forest-700">{formatCurrency(capitalByParty.total_net_cost_basis)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capital Breakdown by Category */}
      {capitalBreakdown.length > 0 && (
        <Card className="rounded-sm border" data-testid="capital-breakdown-chart">
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              Expenses by Category
            </CardTitle>
            <CardDescription>Where have expenses been deployed?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={capitalBreakdown}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={35}
                      fill="#8884d8"
                      dataKey="value"
                      label={renderSmartLabel}
                      labelLine={true}
                    >
                      {capitalBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                <div className="p-3 bg-forest-50 rounded-sm mb-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-heading font-bold">{formatCurrency(totalCapitalUsed)}</p>
                  <p className="text-[10px] text-muted-foreground">Across all projects (excl. purchase & EMD)</p>
                </div>
                {capitalBreakdown.slice(0, 8).map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between p-2 border rounded-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-sm" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono text-sm font-medium">{formatCurrency(item.value)}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({((item.value / totalCapitalUsed) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Reports */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="rounded-sm border" data-testid="portfolio-report-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-forest-50 rounded-sm">
                <TrendingUp className="w-5 h-5 text-forest-500" />
              </div>
              <div>
                <CardTitle className="text-base">Portfolio Report</CardTitle>
                <CardDescription className="text-xs">Projects overview with filters</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => openReportConfig('portfolio', `Portfolio_Report_${new Date().toISOString().split('T')[0]}`)}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
              data-testid="generate-portfolio-report-btn"
            >
              <Filter className="w-4 h-4 mr-2" /> Configure & Generate
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="capital-report-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-muted/30 rounded-sm">
                <PieChart className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Capital Breakdown</CardTitle>
                <CardDescription className="text-xs">Expense categories by project</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => openReportConfig('capital', `Capital_Breakdown_${new Date().toISOString().split('T')[0]}`)}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
              data-testid="generate-capital-report-btn"
            >
              <Filter className="w-4 h-4 mr-2" /> Configure & Generate
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="commission-report-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-terracotta-50 rounded-sm">
                <Wallet className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <CardTitle className="text-base">Commission Report</CardTitle>
                <CardDescription className="text-xs">YTD commission summary</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => openReportConfig('commission', `Commission_Report_${new Date().getFullYear()}`)}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
              data-testid="generate-commission-report-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" /> Configure & Generate
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="jv-summary-report-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-50 rounded-sm">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base">JV Summary</CardTitle>
                <CardDescription className="text-xs">JV agreements with filters</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => openReportConfig('jv', `JV_Summary_${new Date().toISOString().split('T')[0]}`)}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
              data-testid="generate-jv-summary-btn"
            >
              <Filter className="w-4 h-4 mr-2" /> Configure & Generate
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Subdivision Reports */}
      {subdivisionParents.length > 0 && (
        <Card className="rounded-sm border" data-testid="subdivision-reports-card">
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Subdivision Reports
            </CardTitle>
            <CardDescription>
              Generate consolidated financial reports for subdivided properties
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {subdivisionParents.map(parent => {
                const childCount = projects.filter(p => p.parent_project_id === parent.id).length;
                const soldCount = projects.filter(p => p.parent_project_id === parent.id && ['Sold', 'Note_Held', 'Completed'].includes(p.status)).length;
                const jv = jvAgreements.find(j => j.project_id === parent.id);
                
                return (
                  <div 
                    key={parent.id} 
                    className="flex items-center justify-between p-3 border rounded-sm hover:bg-muted/30 transition-colors"
                  >
                    <div>
                      <p className="font-medium">{parent.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {childCount} lots • {soldCount} sold • {formatCurrency(parent.purchase_price)}
                        {jv && ` • JV: ${jv.investor_name}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className={`rounded-sm text-xs ${soldCount === childCount && childCount > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                      >
                        {childCount > 0 ? `${Math.round((soldCount / childCount) * 100)}% Complete` : 'No lots'}
                      </Badge>
                      <Button
                        onClick={() => openSubdivisionReport(parent)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        data-testid={`subdivision-report-btn-${parent.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" /> Report
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Investor Statements */}
      <Card className="rounded-sm border" data-testid="investor-statements-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold uppercase tracking-wide">
            Individual Investor Statements
          </CardTitle>
          <CardDescription>
            Generate investor statements for projects with JV agreements
          </CardDescription>
        </CardHeader>
        <CardContent>
          {projectsWithJV.length > 0 ? (
            <div className="space-y-2">
              {projectsWithJV.map(project => {
                const jv = jvAgreements.find(j => j.project_id === project.id);
                return (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-3 border rounded-sm"
                  >
                    <div>
                      <p className="font-medium">{project.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Investor: {jv?.investor_name} • Capital: {formatCurrency(jv?.capital_contributed)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={project.status} />
                      <Link to={`/projects/${project.id}`}>
                        <Button variant="outline" size="sm" className="rounded-sm">
                          <FileText className="w-4 h-4 mr-1" /> View
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No projects with JV agreements yet.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner-Financed Notes Report */}
      {notesReport && notesReport.summary?.total_notes > 0 && (
        <Card className="rounded-sm border" data-testid="notes-report-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Owner-Financed Notes Report
                </CardTitle>
                <CardDescription>
                  Track all owner-financed deals, payments, and commission status
                </CardDescription>
              </div>
              <Button
                onClick={() => generateNotesReport()}
                variant="outline"
                size="sm"
                className="rounded-sm"
              >
                <Download className="w-4 h-4 mr-1" /> Export PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-white rounded-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Active Notes</span>
                </div>
                <p className="text-xl font-mono font-bold text-blue-600">{notesReport.summary.active_notes}</p>
              </div>
              <div className="p-4 bg-white rounded-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Total Receivable</span>
                </div>
                <p className="text-xl font-mono font-bold text-emerald-600">{formatCurrency(notesReport.summary.total_remaining_balance)}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-sm border border-purple-100">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Expected Monthly</span>
                </div>
                <p className="text-xl font-mono font-bold text-purple-600">{formatCurrency(notesReport.summary.expected_monthly_income)}</p>
              </div>
              <div className="p-4 bg-white rounded-sm border">
                <div className="flex items-center gap-2 mb-1">
                  <Percent className="w-4 h-4 text-amber-600" />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Interest Received</span>
                </div>
                <p className="text-xl font-mono font-bold text-amber-600">{formatCurrency(notesReport.summary.total_interest_received)}</p>
              </div>
            </div>

            {/* YTD Stats */}
            {notesReport.ytd_stats && (
              <div className="mb-6 p-4 bg-muted/30 rounded-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{notesReport.ytd_stats.year} Year-to-Date</p>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Payments Received</p>
                    <p className="font-mono font-semibold">{notesReport.ytd_stats.total_payments}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Amount</p>
                    <p className="font-mono font-semibold">{formatCurrency(notesReport.ytd_stats.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Principal</p>
                    <p className="font-mono font-semibold">{formatCurrency(notesReport.ytd_stats.principal_received)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Interest</p>
                    <p className="font-mono font-semibold">{formatCurrency(notesReport.ytd_stats.interest_received)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes List */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">All Notes</p>
              {notesReport.notes?.map(note => (
                <div key={note.note_id} className="p-4 border rounded-sm hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{note.buyer_name}</p>
                        <Badge variant="outline" className={`rounded-sm text-xs ${
                          note.status === 'Active' ? 'bg-green-50 text-green-700' : 
                          note.status === 'Paid_Off' ? 'bg-blue-50 text-blue-700' : 
                          'bg-red-50 text-red-700'
                        }`}>
                          {note.status}
                        </Badge>
                      </div>
                      <Link to={`/projects/${note.project_id}`} className="text-sm text-blue-600 hover:underline">
                        {note.project_name}
                      </Link>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Remaining</p>
                      <p className="text-lg font-mono font-semibold">{formatCurrency(note.remaining_balance)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Principal</p>
                      <p className="font-mono">{formatCurrency(note.principal_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Interest Rate</p>
                      <p className="font-mono">{(note.interest_rate * 100).toFixed(2)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Monthly</p>
                      <p className="font-mono">{formatCurrency(note.monthly_payment)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Received</p>
                      <p className="font-mono text-emerald-600">{formatCurrency(note.total_received)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Payments</p>
                      <p className="font-mono">{note.payments_count} payments</p>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Payment Progress</span>
                      <span>{((note.total_received / note.principal_amount) * 100).toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div 
                        className="bg-emerald-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (note.total_received / note.principal_amount) * 100)}%` }}
                      />
                    </div>
                  </div>
                  {/* Commission Tracking */}
                  {note.commission_tracking && note.commission_tracking.total_commission_owed > 0 && (
                    <div className="mt-3 pt-3 border-t text-xs">
                      <span className="text-muted-foreground">Commission: </span>
                      <span className="font-mono">{formatCurrency(note.commission_tracking.total_commission_paid)}</span>
                      <span className="text-muted-foreground"> / </span>
                      <span className="font-mono">{formatCurrency(note.commission_tracking.total_commission_owed)}</span>
                      <span className="text-muted-foreground"> paid</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dead Deals Report */}
      <Card className="rounded-sm border" data-testid="dead-deals-report-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                <Skull className="w-4 h-4 text-red-500" />
                Dead Deals Report
              </CardTitle>
              <CardDescription>
                {deadDealsReport?.period_label || 'All Time'} — Track deals that fell through and their associated losses
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Period Selector */}
              <select
                value={deadDealsPeriod}
                onChange={(e) => setDeadDealsPeriod(e.target.value)}
                className="h-8 rounded-sm border bg-background px-2 text-xs font-medium"
                data-testid="dead-deals-period-select"
              >
                <option value="all_time">All Time</option>
                <option value="ytd">Year-to-Date</option>
                <option value="year">By Year</option>
                <option value="custom">Custom Range</option>
              </select>
              {deadDealsPeriod === 'year' && (
                <select
                  value={deadDealsYear}
                  onChange={(e) => setDeadDealsYear(Number(e.target.value))}
                  className="h-8 rounded-sm border bg-background px-2 text-xs font-medium"
                  data-testid="dead-deals-year-select"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              )}
              {deadDealsPeriod === 'custom' && (
                <div className="flex items-center gap-1">
                  <input
                    type="date"
                    value={deadDealsCustomStart}
                    onChange={(e) => setDeadDealsCustomStart(e.target.value)}
                    className="h-8 rounded-sm border bg-background px-2 text-xs"
                    data-testid="dead-deals-custom-start"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="date"
                    value={deadDealsCustomEnd}
                    onChange={(e) => setDeadDealsCustomEnd(e.target.value)}
                    className="h-8 rounded-sm border bg-background px-2 text-xs"
                    data-testid="dead-deals-custom-end"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-sm text-xs"
                    onClick={() => loadDeadDeals('custom', null, deadDealsCustomStart, deadDealsCustomEnd)}
                    disabled={!deadDealsCustomStart || !deadDealsCustomEnd}
                    data-testid="dead-deals-custom-apply"
                  >
                    Apply
                  </Button>
                </div>
              )}
              {loadingDeadDeals && <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>}
              {deadDealsReport && deadDealsReport.summary?.total_dead_deals > 0 && (
                <Button
                  onClick={() => generateDeadDealsReport()}
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  data-testid="dead-deals-export-btn"
                >
                  <Download className="w-4 h-4 mr-1" /> Export PDF
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {deadDealsReport && deadDealsReport.summary?.total_dead_deals > 0 ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-red-50 rounded-sm border border-red-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Skull className="w-4 h-4 text-red-600" />
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Dead Deals</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-red-600" data-testid="dead-deals-count">{deadDealsReport.summary.total_dead_deals}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-sm border border-orange-100">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-orange-600" />
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Out-of-Pocket</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-orange-600" data-testid="dead-deals-expenses">{formatCurrency(deadDealsReport.summary.total_out_of_pocket || 0)}</p>
                  <p className="text-[10px] text-muted-foreground">EMD + expenses</p>
                </div>
                <div className="p-4 bg-white rounded-sm border">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Refunds</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-emerald-600">{formatCurrency(deadDealsReport.summary.total_refunds || 0)}</p>
                </div>
                <div className="p-4 bg-red-100 rounded-sm border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-red-700 rotate-180" />
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Net Sunk Costs</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-red-700">{formatCurrency(deadDealsReport.summary.total_losses || 0)}</p>
                </div>
              </div>

              {/* TRENDS ANALYSIS */}
              {deadDealsReport.trends && (
                <div className="mb-6" data-testid="dead-deals-trends">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Trend Analysis — Marketing Insights
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* By State */}
                    {deadDealsReport.trends.by_state?.length > 0 && (
                      <div className="p-4 border rounded-sm" data-testid="trends-by-state">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">By State</p>
                        <div className="space-y-2">
                          {deadDealsReport.trends.by_state.map(s => (
                            <div key={s.state} className="flex items-center justify-between">
                              <span className="text-sm font-medium">{s.state}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground">{formatCurrency(s.total_price)}</span>
                                <Badge variant="outline" className="rounded-sm text-xs bg-red-50 text-red-700 font-mono">{s.count} deal{s.count !== 1 ? 's' : ''}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* By Acreage */}
                    {deadDealsReport.trends.by_acreage?.length > 0 && (
                      <div className="p-4 border rounded-sm" data-testid="trends-by-acreage">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">By Acreage</p>
                        <div className="space-y-2">
                          {deadDealsReport.trends.by_acreage.map(a => {
                            const maxCount = Math.max(...deadDealsReport.trends.by_acreage.map(x => x.count));
                            const pct = maxCount > 0 ? (a.count / maxCount) * 100 : 0;
                            return (
                              <div key={a.range}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm">{a.range}</span>
                                  <span className="text-sm font-mono font-semibold">{a.count}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-1.5">
                                  <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {deadDealsReport.trends.averages && (
                          <p className="text-xs text-muted-foreground mt-3">Avg: {Number(deadDealsReport.trends.averages.avg_acreage || 0).toFixed(2)} acres</p>
                        )}
                      </div>
                    )}

                    {/* By Price Range */}
                    {deadDealsReport.trends.by_price?.length > 0 && (
                      <div className="p-4 border rounded-sm" data-testid="trends-by-price">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">By Price Range</p>
                        <div className="space-y-2">
                          {deadDealsReport.trends.by_price.map(p => {
                            const maxCount = Math.max(...deadDealsReport.trends.by_price.map(x => x.count));
                            const pct = maxCount > 0 ? (p.count / maxCount) * 100 : 0;
                            return (
                              <div key={p.range}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm">{p.range}</span>
                                  <span className="text-sm font-mono font-semibold">{p.count}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-1.5">
                                  <div className="bg-red-400 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {deadDealsReport.trends.averages && (
                          <p className="text-xs text-muted-foreground mt-3">Avg: {formatCurrency(deadDealsReport.trends.averages.avg_price)}</p>
                        )}
                      </div>
                    )}

                    {/* By County (Location Detail) */}
                    {deadDealsReport.trends.by_county?.length > 0 && (
                      <div className="p-4 border rounded-sm" data-testid="trends-by-county">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">By County</p>
                        <div className="space-y-2">
                          {deadDealsReport.trends.by_county.map(c => (
                            <div key={c.location} className="flex items-center justify-between">
                              <span className="text-sm">{c.location}</span>
                              <Badge variant="outline" className="rounded-sm text-xs font-mono">{c.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* By Strategy */}
                    {deadDealsReport.trends.by_strategy?.length > 0 && (
                      <div className="p-4 border rounded-sm" data-testid="trends-by-strategy">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">By Strategy</p>
                        <div className="space-y-2">
                          {deadDealsReport.trends.by_strategy.map(s => (
                            <div key={s.strategy} className="flex items-center justify-between">
                              <span className="text-sm">{s.strategy}</span>
                              <Badge variant="outline" className="rounded-sm text-xs font-mono">{s.count} deal{s.count !== 1 ? 's' : ''}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Summary Averages */}
                    {deadDealsReport.trends.averages && (
                      <div className="p-4 border rounded-sm bg-muted/20" data-testid="trends-averages">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Key Averages</p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Avg Acreage</p>
                            <p className="text-lg font-mono font-semibold">{Number(deadDealsReport.trends.averages.avg_acreage || 0).toFixed(2)} acres</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Total Acreage Lost</p>
                            <p className="text-lg font-mono font-semibold">{Number(deadDealsReport.trends.averages.total_acreage || 0).toFixed(2)} acres</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Dead Deals List */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">All Dead Deals</p>
                {deadDealsReport.deals?.map(deal => (
                  <div key={deal.project_id} className="p-4 border rounded-sm hover:bg-muted/30 transition-colors" data-testid={`dead-deal-${deal.project_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Link to={`/projects/${deal.project_id}`} className="font-medium text-blue-600 hover:underline">
                            {deal.project_name}
                          </Link>
                          <Badge variant="outline" className="rounded-sm text-xs bg-red-50 text-red-700">
                            Dead
                          </Badge>
                          {deal.strategy && (
                            <Badge variant="outline" className="rounded-sm text-xs">{deal.strategy}</Badge>
                          )}
                        </div>
                        {(deal.county || deal.state) && (
                          <p className="text-sm text-muted-foreground">{[deal.county, deal.state].filter(Boolean).join(', ')} {deal.acres ? `\u2022 ${Number(deal.acres).toFixed(2)} acres` : ''}</p>
                        )}
                      </div>
                      {(deal.total_out_of_pocket > 0 || deal.net_loss > 0) && (
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Net Loss</p>
                          <p className="text-lg font-mono font-semibold text-red-600">{formatCurrency(deal.net_loss || 0)}</p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Out-of-Pocket</p>
                        <p className="font-mono text-red-600">{formatCurrency(deal.total_out_of_pocket || 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Refunds</p>
                        <p className="font-mono text-emerald-600">{formatCurrency(deal.total_refunds || 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Acquisition Date</p>
                        <p className="font-mono">{formatDate(deal.acquisition_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Date Killed</p>
                        <p className="font-mono">{formatDate(deal.dead_date)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <Skull className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p>No dead deals recorded yet.</p>
              <p className="text-xs mt-1">Deals that fall through will appear here with their associated losses.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Configure Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Report Name */}
            <div className="space-y-2">
              <Label>Report File Name</Label>
              <Input
                value={reportConfig.name}
                onChange={(e) => setReportConfig({ ...reportConfig, name: e.target.value })}
                className="rounded-sm"
                placeholder="Enter report name"
              />
              <p className="text-xs text-muted-foreground">.pdf will be added automatically</p>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Date Range
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Input
                    type="date"
                    value={reportConfig.dateFrom}
                    onChange={(e) => setReportConfig({ ...reportConfig, dateFrom: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Input
                    type="date"
                    value={reportConfig.dateTo}
                    onChange={(e) => setReportConfig({ ...reportConfig, dateTo: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Leave empty for all time</p>
            </div>

            {/* Project Scope */}
            <div className="space-y-2">
              <Label>Project Scope</Label>
              <Select
                value={reportConfig.scope}
                onValueChange={(value) => setReportConfig({ ...reportConfig, scope: value, selectedProjects: [] })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="selected">Selected Projects Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Project Selection */}
            {reportConfig.scope === 'selected' && (
              <div className="space-y-2">
                <Label>Select Projects ({reportConfig.selectedProjects.length} selected)</Label>
                <div className="border rounded-sm max-h-[200px] overflow-y-auto">
                  {projects.map(project => (
                    <div
                      key={project.id}
                      className="flex items-center gap-3 p-2 hover:bg-muted/30 cursor-pointer"
                      onClick={() => toggleProjectSelection(project.id)}
                    >
                      <Checkbox
                        checked={reportConfig.selectedProjects.includes(project.id)}
                        onCheckedChange={() => toggleProjectSelection(project.id)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{project.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {project.status.replace('_', ' ')} • {project.strategy}
                        </p>
                      </div>
                      <StatusBadge status={project.status} size="sm" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowConfigDialog(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button 
                onClick={handleGenerateReport}
                className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                disabled={reportConfig.scope === 'selected' && reportConfig.selectedProjects.length === 0}
              >
                <Download className="w-4 h-4 mr-2" /> Generate Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subdivision Report Dialog */}
      <Dialog open={showSubdivisionDialog} onOpenChange={setShowSubdivisionDialog}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Subdivision Report: {selectedSubdivisionParent?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingSubdivision ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : subdivisionFinancials ? (
            <div className="space-y-6">
              {/* Completion Status */}
              <div className="p-4 bg-muted/30 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Completion Progress</span>
                  <span className="text-sm font-mono">
                    {subdivisionFinancials.totals.sold_lots} / {subdivisionFinancials.totals.total_lots} lots sold
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-forest-500 h-2 rounded-full transition-all"
                    style={{ width: `${subdivisionFinancials.completion_status.percent_complete}%` }}
                  />
                </div>
              </div>

              {/* Parent Overview */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-3">Parent Lot Overview</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-muted/30 rounded-sm">
                    <p className="text-xs text-muted-foreground">Purchase Price</p>
                    <p className="text-lg font-mono font-semibold">{formatCurrency(subdivisionFinancials.parent_project.total_purchase_price)}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-sm">
                    <p className="text-xs text-muted-foreground">Acquisition Date</p>
                    <p className="text-lg font-mono font-semibold">{subdivisionFinancials.parent_project.acquisition_date || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-sm">
                    <p className="text-xs text-muted-foreground">Total Sale Revenue</p>
                    <p className="text-lg font-mono font-semibold">{formatCurrency(subdivisionFinancials.totals.total_sale_price)}</p>
                  </div>
                </div>
              </div>

              {/* JV Summary */}
              {subdivisionFinancials.jv_summary.has_jv && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide mb-3">JV Investor Summary</h3>
                  <div className="p-4 border rounded-sm bg-blue-50/50">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Investor</p>
                        <p className="font-medium">{subdivisionFinancials.jv_summary.investors.map(i => i.name).join(', ')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Capital</p>
                        <p className="font-mono font-medium">{formatCurrency(subdivisionFinancials.jv_summary.total_jv_capital)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Investor Profit</p>
                        <p className="font-mono font-medium text-emerald-600">{formatCurrency(subdivisionFinancials.jv_summary.investor_profit)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Return to Investor</p>
                        <p className="font-mono font-medium">{formatCurrency(subdivisionFinancials.jv_summary.investor_total_return)}</p>
                      </div>
                    </div>
                    {subdivisionFinancials.jv_summary.active_tier && (
                      <div className="mt-3 pt-3 border-t">
                        <Badge variant="outline" className="rounded-sm">
                          Tier {subdivisionFinancials.jv_summary.active_tier} ({subdivisionFinancials.jv_summary.investor_split_percentage}% investor split)
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Child Lots Table */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-3">Child Lots Detail</h3>
                <div className="border rounded-sm overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Lot</th>
                        <th>Status</th>
                        <th>Cost %</th>
                        <th>Cost Basis</th>
                        <th>Sale Price</th>
                        <th>Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subdivisionFinancials.children_summary.map((child) => (
                        <tr key={child.id}>
                          <td className="font-medium">{child.name}</td>
                          <td>
                            <Badge 
                              variant="outline" 
                              className={`rounded-sm text-xs ${child.is_sold ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                            >
                              {child.status.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="font-mono">{child.cost_basis_percentage}%</td>
                          <td className="font-mono">{formatCurrency(child.allocated_cost_basis)}</td>
                          <td className="font-mono">{child.is_sold ? formatCurrency(child.sale_price) : '-'}</td>
                          <td className="font-mono">
                            {child.is_sold ? (
                              <span className={child.sale_price - child.allocated_cost_basis >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                {formatCurrency(child.sale_price - child.allocated_cost_basis)}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Land Finder Summary - Internal Use Only */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-3">Land Finder Returns</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-forest-50 rounded-sm">
                    <p className="text-xs text-muted-foreground">Land Finder Profit Share</p>
                    <p className="text-lg font-mono font-semibold">{formatCurrency(subdivisionFinancials.land_finder_summary.land_finder_profit)}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-sm">
                    <p className="text-xs text-muted-foreground">Gross Profit</p>
                    <p className="text-lg font-mono font-semibold">{formatCurrency(subdivisionFinancials.totals.gross_profit)}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowSubdivisionDialog(false)} className="rounded-sm">
                  Close
                </Button>
                <Button 
                  onClick={generateSubdivisionReport}
                  className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              Failed to load subdivision data
            </div>
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="financial" className="mt-4">
          <FinancialReports embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
};
